import React from 'react';
import { formatCurrency } from 'utils';
import { Stack, Typography } from '@mui/material';

import { FieldChange } from './FieldChange';
import { PricingMarkupChanges } from '../../../../types';

type PricingParams = NonNullable<NonNullable<PricingMarkupChanges['pricing']>['before']>[string];

const PRICING_PARAM_LABELS: Record<string, string> = {
  MARKUP: 'Markup',
  DEALER_COST: 'Dealer Cost',
  PRICING_MAX: 'Pricing Max',
  PRICING_MIN: 'Pricing Min',
  RATE_FACTOR: 'Rate Factor',
  SUGGESTED_RETAIL_PRICE: 'Suggested Retail Price',
};

const formatFieldValue = (key: string, val: unknown): string => {
  if (val == null) return '—';

  if (key === 'MARKUP' && typeof val === 'object') {
    const markup = val as { TYPE: string; AMOUNT: number };
    return `${markup.TYPE} ${markup.TYPE === 'FIXED' ? formatCurrency(markup.AMOUNT, true) : `(${markup.AMOUNT})%`}`;
  }

  return formatCurrency(val, true);
};

interface PricingMarkupRenderProps {
  changes: PricingMarkupChanges;
}

export const PricingMarkupRender = ({ changes }: PricingMarkupRenderProps) => {
  if (!changes.pricing) return null;

  const { before, after } = changes.pricing;

  const allKeys = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]));

  if (!allKeys.length) return null;

  return (
    <Stack gap="0.5rem">
      {allKeys.map(termKey => {
        const bParams = (before?.[termKey] ?? {}) as PricingParams;
        const aParams = (after?.[termKey] ?? {}) as PricingParams;

        const fieldKeys = Array.from(new Set([...Object.keys(bParams), ...Object.keys(aParams)]));

        const changedFields = fieldKeys.filter(
          field =>
            formatFieldValue(field, bParams[field as keyof PricingParams]) !==
            formatFieldValue(field, aParams[field as keyof PricingParams])
        );

        if (!changedFields.length) return null;

        return (
          <Stack key={termKey} gap="0.25rem">
            <Typography>Configuration {termKey}</Typography>

            {changedFields.map(field => (
              <FieldChange
                key={field}
                label={PRICING_PARAM_LABELS[field] ?? field}
                before={formatFieldValue(field, bParams[field as keyof PricingParams])}
                after={formatFieldValue(field, aParams[field as keyof PricingParams])}
              />
            ))}
          </Stack>
        );
      })}
    </Stack>
  );
};
